import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * ExpiryBanner — global "Your access has expired" / "Trial ends in N days"
 * banner. Rendered once below the Navbar via App.js so the message is
 * impossible to miss without being intrusive on every page individually.
 *
 * Reads `valid_until` from localStorage (stashed by Login.jsx after the
 * /api/auth/login response). Admins never see this — the login payload
 * resolves `is_expired` server-side and omits the date for admin users.
 *
 * The banner does NOT block any UI itself — the actual reading endpoints
 * are gated server-side. This is purely the user-facing notice.
 */
const ExpiryBanner = () => {
  const [state, setState] = useState({ show: false });

  useEffect(() => {
    const check = () => {
      try {
        const raw = localStorage.getItem('valid_until');
        const role = localStorage.getItem('role');
        if (!raw || raw === 'null' || role === 'admin') {
          setState({ show: false });
          return;
        }
        const expiry = new Date(raw);
        if (Number.isNaN(expiry.getTime())) {
          setState({ show: false });
          return;
        }
        const now = new Date();
        const msPerDay = 24 * 60 * 60 * 1000;
        const daysLeft = Math.ceil((expiry - now) / msPerDay);

        if (daysLeft <= 0) {
          // Grace period — 7 days after valid_until during which the user
          // can still log in (to see this message) but cannot read books.
          // After 7 days the backend cron flips status to 'suspended'.
          const graceMs = 7 * msPerDay;
          const graceEnd = new Date(expiry.getTime() + graceMs);
          const graceDaysLeft = Math.ceil((graceEnd - now) / msPerDay);
          setState({
            show: true,
            expired: true,
            expiry,
            graceDaysLeft: Math.max(0, graceDaysLeft),
            inGrace: graceDaysLeft > 0,
          });
        } else if (daysLeft <= 3) {
          // Show a gentle reminder during the last 72 hours
          setState({ show: true, expired: false, daysLeft, expiry });
        } else {
          setState({ show: false });
        }
      } catch {
        setState({ show: false });
      }
    };
    check();
    // Re-check every minute so the banner flips to "expired" on time
    // without requiring a full page reload.
    const t = setInterval(check, 60_000);
    return () => clearInterval(t);
  }, []);

  if (!state.show) return null;

  return (
    <div
      className={`relative z-50 w-full px-4 py-2.5 text-sm font-medium border-b backdrop-blur-sm ${
        state.expired
          ? 'bg-red-600/40 border-red-500/60 text-red-50'
          : 'bg-amber-500/30 border-amber-400/60 text-amber-50'
      }`}
      data-testid="account-expiry-banner"
      role="alert"
    >
      <div className="max-w-screen-2xl mx-auto flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
        {state.expired ? (
          state.inGrace ? (
            <span>
              <strong>Your access has expired.</strong> You have {state.graceDaysLeft} day{state.graceDaysLeft === 1 ? '' : 's'} of grace left to renew before your account is suspended. Reading is currently blocked. Contact admin —{' '}
              <a href="mailto:info@sevenchapterbooks.com" className="underline hover:text-white">
                info@sevenchapterbooks.com
              </a>
            </span>
          ) : (
            <span>
              <strong>Your access has expired and the 7-day grace period has run out.</strong> Your account will be suspended at the next sweep. Please contact admin to renew —{' '}
              <a href="mailto:info@sevenchapterbooks.com" className="underline hover:text-white">
                info@sevenchapterbooks.com
              </a>
            </span>
          )
        ) : (
          <span>
            Your access expires in <strong>{state.daysLeft} day{state.daysLeft === 1 ? '' : 's'}</strong>
            {' '}(on {state.expiry.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}).
            Reach out to the admin to extend your subscription.
          </span>
        )}
      </div>
    </div>
  );
};

export default ExpiryBanner;
